import React, { useMemo, useState } from 'react';
import { ArrowLeft, Check, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { ulumAsatizahAdmin } from '../data/mockData';
import MorphButton from '../components/MorphButton';
import { requireAuth } from '../components/AuthGate';
import { sgToday } from '../lib/dates';

const fieldClass =
  'mt-1.5 w-full h-11 rounded-xl bg-muted/60 px-3 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-primary/40';

// Was a private copy of the same helper; dates come from one place now.
const todayISO = sgToday;

function FieldLabel({ htmlFor, children, hint }) {
  return (
    <label className="text-xs font-semibold text-[#0F172A]" htmlFor={htmlFor}>
      {children}
      {hint ? <span className="font-normal text-muted-foreground"> {hint}</span> : null}
    </label>
  );
}

export default function UlumAsatizahForm({ onBack, onDone }) {
  const { durations, startTimes, hosts, lessons, classes } = ulumAsatizahAdmin;
  const defaultClass = classes[0];

  const [date, setDate] = useState(todayISO);
  const [startTime, setStartTime] = useState('19:30');
  const [duration, setDuration] = useState(1);
  const [hostId, setHostId] = useState(hosts[0].id);
  const [classId, setClassId] = useState(defaultClass.id);
  const [lessonId, setLessonId] = useState(lessons[0].id);
  const [presentIds, setPresentIds] = useState(() => defaultClass.students.map((s) => s.id));
  const [remarks, setRemarks] = useState('');
  const [photoName, setPhotoName] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const selectedClass = useMemo(
    () => classes.find((c) => c.id === classId) || classes[0],
    [classes, classId]
  );
  const host = hosts.find((h) => h.id === hostId);
  const lesson = lessons.find((l) => l.id === lessonId);
  const startLabel = startTimes.find((t) => t.id === startTime)?.label || startTime;
  const presentCount = presentIds.length;
  const rosterSize = selectedClass.students.length;

  const handleClassChange = (nextId) => {
    const next = classes.find((c) => c.id === nextId) || classes[0];
    setClassId(next.id);
    setPresentIds(next.students.map((s) => s.id));
  };

  const toggleStudent = (id) => {
    setPresentIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (!date || !hostId || !classId || !lessonId || !duration) {
      toast.error('Please complete date, duration, host, class and lesson', { position: 'top-center' });
      return false;
    }
    if (!confirmed) {
      toast.error('Please confirm this session took place', { position: 'top-center' });
      return false;
    }
    return requireAuth();
  };

  return (
    <div className="min-h-screen bg-background md:bg-[#F3F6F5] page-fade-in" data-testid="ulum-asatizah-form">
      <div className="bg-white px-4 pt-4 pb-3 flex items-center gap-3 border-b border-border/60">
        <button type="button" data-testid="ulum-asatizah-back" onClick={onBack} aria-label="Back">
          <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
        </button>
        <span className="text-base font-bold text-[#0F172A] truncate">Ulum Ihsan Asatizah</span>
      </div>

      <div className="md:max-w-[560px] md:mx-auto md:my-10 md:bg-white md:rounded-2xl md:shadow-lg md:border md:border-border/60">
        <div className="px-4 pt-5 pb-3 md:px-8">
          <h1 className="text-2xl font-extrabold text-[#0F172A]">Log this session</h1>
          <p className="text-sm text-muted-foreground mt-1">
            One form per class. Hours and host here become the timesheet finance uses to pay you.
          </p>
        </div>

        <div className="px-4 md:px-8 flex flex-col gap-5 pb-2">
          <div>
            <FieldLabel htmlFor="ulum-session-date">Session date</FieldLabel>
            <input
              id="ulum-session-date"
              type="date"
              data-testid="ulum-session-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={fieldClass}
            />
          </div>

          <div>
            <FieldLabel htmlFor="ulum-session-time">Start time</FieldLabel>
            <select
              id="ulum-session-time"
              data-testid="ulum-session-time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className={fieldClass}
            >
              {startTimes.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-xs font-semibold text-[#0F172A]">Duration</p>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {durations.map((d) => {
                const active = duration === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    data-testid={`ulum-duration-${d.id}`}
                    aria-pressed={active}
                    onClick={() => setDuration(d.id)}
                    className={`h-11 rounded-xl text-sm font-semibold transition-colors ${
                      active ? 'bg-primary text-white' : 'bg-muted/60 text-[#0F172A] hover:bg-muted'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <FieldLabel htmlFor="ulum-session-host">Host location</FieldLabel>
            <select
              id="ulum-session-host"
              data-testid="ulum-session-host"
              value={hostId}
              onChange={(e) => setHostId(e.target.value)}
              className={fieldClass}
            >
              {hosts.map((h) => (
                <option key={h.id} value={h.id}>{h.name} — {h.area}</option>
              ))}
            </select>
          </div>

          <div>
            <FieldLabel htmlFor="ulum-session-class">Class</FieldLabel>
            <select
              id="ulum-session-class"
              data-testid="ulum-session-class"
              value={classId}
              onChange={(e) => handleClassChange(e.target.value)}
              className={fieldClass}
            >
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.title} ({c.schedule})</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-end justify-between gap-3">
              <p className="text-xs font-semibold text-[#0F172A]">
                Roll call
                <span className="font-normal text-muted-foreground"> — uncheck absentees</span>
              </p>
              <span className="text-[11px] font-semibold text-primary" data-testid="ulum-present-count">
                {presentCount} of {rosterSize} present
              </span>
            </div>
            <div className="mt-2 flex flex-col gap-2" data-testid="ulum-roll-call">
              {selectedClass.students.map((student) => {
                const isPresent = presentIds.includes(student.id);
                return (
                  <button
                    key={student.id}
                    type="button"
                    data-testid={`ulum-student-${student.id}`}
                    onClick={() => toggleStudent(student.id)}
                    aria-pressed={isPresent}
                    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                      isPresent ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
                        isPresent ? 'border-primary bg-primary text-white' : 'border-muted-foreground/40'
                      }`}
                    >
                      {isPresent && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    <span className={`text-sm font-medium ${isPresent ? 'text-[#0F172A]' : 'text-muted-foreground line-through'}`}>
                      {student.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <FieldLabel htmlFor="ulum-session-lesson">Lesson covered</FieldLabel>
            <select
              id="ulum-session-lesson"
              data-testid="ulum-session-lesson"
              value={lessonId}
              onChange={(e) => setLessonId(e.target.value)}
              className={fieldClass}
            >
              {lessons.map((l) => (
                <option key={l.id} value={l.id}>{l.title}</option>
              ))}
            </select>
          </div>

          <div>
            <FieldLabel htmlFor="ulum-session-photo" hint="(optional proof)">Class photo</FieldLabel>
            <label
              htmlFor="ulum-session-photo"
              className="mt-1.5 flex h-11 w-full items-center gap-2 rounded-xl bg-muted/60 px-3 text-sm text-muted-foreground cursor-pointer"
            >
              <Camera className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{photoName || 'Attach a class photo'}</span>
            </label>
            <input
              id="ulum-session-photo"
              type="file"
              accept="image/*"
              data-testid="ulum-session-photo"
              className="sr-only"
              onChange={(e) => setPhotoName(e.target.files?.[0]?.name || '')}
            />
          </div>

          <div>
            <FieldLabel htmlFor="ulum-session-remarks" hint="(optional)">Remarks</FieldLabel>
            <textarea
              id="ulum-session-remarks"
              data-testid="ulum-session-remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              placeholder="Anything about the session, host or learners…"
              className="mt-1.5 w-full rounded-xl bg-muted/60 p-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <button
            type="button"
            data-testid="ulum-session-confirm"
            onClick={() => setConfirmed((v) => !v)}
            aria-pressed={confirmed}
            className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${
              confirmed ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
            }`}
          >
            <span
              className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
                confirmed ? 'border-primary bg-primary text-white' : 'border-muted-foreground/40'
              }`}
            >
              {confirmed && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
            </span>
            <span className="text-sm font-medium text-[#0F172A]">
              I confirm this session took place at this host, for the hours logged above.
            </span>
          </button>
        </div>

        <div className="px-4 md:px-8 pt-5 pb-8">
          <MorphButton
            data-testid="ulum-session-submit"
            idleLabel="Submit session"
            successLabel="Session logged"
            onClick={submit}
            onComplete={() => onDone?.({
              date,
              startLabel,
              duration,
              hostName: host?.name,
              classTitle: selectedClass.title,
              lessonTitle: lesson?.title,
              presentCount,
              rosterSize
            })}
            className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover"
          />
          <p className="mt-3 text-center text-[11px] leading-snug text-muted-foreground">
            This record is what finance uses for your monthly timesheet. No extra job form after this.
          </p>
        </div>
      </div>
    </div>
  );
}
